import type {
  ConversationParticipant as WireConversationParticipant,
  Message as WireMessage,
} from '../services/messenger.wire'

/**
 * Pure unread-count rule for a conversation's loaded thread, pulled out of
 * `selectUiConversations` so it can be unit tested on its own.
 *
 * `undefined` means "nothing loaded to count": the caller's 0/1 heuristic
 * takes over instead (see `wireToUiConversation`). A defined return is the
 * number of messages from someone else, not deleted, newer than my own
 * `lastReadAt` cursor.
 *
 * When I'm not one of the conversation's participants: `callerId` itself is
 * unknown, or the loaded `participants` row for me just isn't there (stale
 * mapping, or hydration that hasn't caught up yet), there is no cursor to
 * count against. Counting every loaded message as unread in that case used to
 * show the whole history as a single giant badge that opening the chat could
 * never clear, since there was no `lastReadAt` for it to compare against.
 * Reporting 0 instead is honest about what we don't know; the caller is
 * expected to refetch the conversation so the real participant row comes
 * back and the count becomes meaningful again.
 */
export function unreadCountFor(
  thread: WireMessage[] | undefined,
  participants: WireConversationParticipant[] | undefined,
  callerId: string | null,
): number | undefined {
  if (!thread || thread.length === 0) return undefined
  if (!callerId) return 0
  const me = participants?.find((p) => p.userId === callerId)
  if (!me) return 0

  const myLastRead = me.lastReadAt
  let count = 0
  for (const m of thread) {
    if (m.senderId && m.senderId !== callerId && !m.deletedAt && (!myLastRead || m.createdAt > myLastRead)) {
      count++
    }
  }
  return count
}
